/**
 * Simulator Main Component
 * Entry point for the Warehouse Simulator module
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Play, Square, Pause, Settings, RefreshCw, CheckCircle, XCircle,
  AlertTriangle, Info, Zap, Users, Activity, Clock
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { SimulatorEngine, getSimulatorEngine, resetSimulatorEngine } from './services/SimulatorEngine';
import {
  SimulatorState,
  SimulatorConfig,
  PreFlightReport,
  PreFlightValidation,
  DEFAULT_SIMULATOR_CONFIG
} from './types';
import { PreFlightValidatorView } from './components/PreFlightValidator';
import { SimulatorDashboard } from './SimulatorDashboard';
import { SimulatorConfigPanel } from './components/SimulatorConfigPanel';

export const SimulatorMain: React.FC = () => {
  const { user } = useAuth();
  const [engine, setEngine] = useState<SimulatorEngine | null>(null);
  const [state, setState] = useState<SimulatorState | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check access
  const hasAccess = user?.operatorGroupId === '3'; // Sysadmin only

  // Initialize engine
  useEffect(() => {
    if (!hasAccess) return;

    const initialConfig: Partial<SimulatorConfig> = {
      companyNo: user?.companyNo || '',
      operatorGroupId: user?.operatorGroupId || '',
      warehouseId: '' // Will be set after warehouse selection
    };

    const eng = getSimulatorEngine(initialConfig);
    setEngine(eng);

    // Subscribe to state changes
    const unsubscribe = eng.onStateChange((newState) => {
      setState(newState);
    });

    // Set initial state
    setState(eng.getState());

    return () => {
      unsubscribe();
    };
  }, [hasAccess, user]);

  // Handle initialization
  const handleInitialize = useCallback(async () => {
    if (!engine) return;

    // Check if warehouse is selected
    if (!state?.config.warehouseId) {
      setError('Du må velge et lager før validering kan kjøres. Åpne Innstillinger for å velge lager.');
      setShowConfig(true);
      return;
    }

    setIsInitializing(true);
    setError(null);

    try {
      await engine.initialize();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Initialisering feilet');
    } finally {
      setIsInitializing(false);
    }
  }, [engine, state?.config.warehouseId]);

  // Handle start
  const handleStart = useCallback(async () => {
    if (!engine) return;

    // Check if warehouse is selected
    if (!state?.config.warehouseId) {
      setError('Du må velge et lager før simulering kan starte. Åpne Innstillinger for å velge lager.');
      setShowConfig(true);
      return;
    }

    setError(null);
    try {
      await engine.start();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke starte simulering');
    }
  }, [engine, state?.config.warehouseId]);

  // Handle stop
  const handleStop = useCallback(async () => {
    if (!engine) return;

    try {
      await engine.stop();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke stoppe simulering');
    }
  }, [engine]);

  // Handle pause/resume
  const handlePauseResume = useCallback(() => {
    if (!engine || !state) return;

    if (state.status === 'running') {
      engine.pause();
    } else if (state.status === 'paused') {
      engine.resume();
    }
  }, [engine, state]);

  // Handle reset
  const handleReset = useCallback(async () => {
    await resetSimulatorEngine();

    const initialConfig: Partial<SimulatorConfig> = {
      companyNo: user?.companyNo || '',
      operatorGroupId: user?.operatorGroupId || '',
      warehouseId: ''
    };

    const eng = getSimulatorEngine(initialConfig);
    setEngine(eng);
    setState(eng.getState());
  }, [user]);

  // Handle config save
  const handleConfigSave = useCallback((config: Partial<SimulatorConfig>) => {
    if (!engine) return;
    engine.updateConfig(config);
    setShowConfig(false);
  }, [engine]);

  // No access view
  if (!hasAccess) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Ingen Tilgang</h2>
          <p className="text-gray-600">
            Warehouse Simulator er kun tilgjengelig for systemadministratorer.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (!engine || !state) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Laster simulator...</p>
        </div>
      </div>
    );
  }

  // Render based on status
  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Zap className="w-8 h-8 text-purple-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-800">Warehouse Simulator</h1>
              <p className="text-sm text-gray-500">Compass v6.0</p>
            </div>
          </div>

          {/* Status Badge */}
          <StatusBadge status={state.status} />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {state.status === 'idle' && (
            <>
              <button
                onClick={handleInitialize}
                disabled={isInitializing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isInitializing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Valider
              </button>
              <button
                onClick={() => setShowConfig(true)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Innstillinger
              </button>
            </>
          )}

          {state.status === 'validating' && (
            <div className="flex items-center gap-2 text-blue-600">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Validerer...</span>
            </div>
          )}

          {(state.status === 'idle' && state.preFlightReport) && (
            <>
              <button
                onClick={() => setShowConfig(true)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Innstillinger
              </button>
              <button
                onClick={handleStart}
                disabled={!state.preFlightReport.canStart}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                  state.preFlightReport.canStart
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Play className="w-4 h-4" />
                Start Simulering
              </button>
            </>
          )}

          {(state.status === 'running' || state.status === 'paused') && (
            <>
              <button
                onClick={handlePauseResume}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2"
              >
                {state.status === 'running' ? (
                  <>
                    <Pause className="w-4 h-4" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Fortsett
                  </>
                )}
              </button>
              <button
                onClick={handleStop}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                <Square className="w-4 h-4" />
                Stopp
              </button>
            </>
          )}

          {(state.status === 'stopped' || state.status === 'error') && (
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Tilbakestill
            </button>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Pre-flight validation view */}
        {state.status === 'idle' && state.preFlightReport && (
          <PreFlightValidatorView
            report={state.preFlightReport}
          />
        )}

        {/* Welcome view */}
        {state.status === 'idle' && !state.preFlightReport && (
          <WelcomeView
            onStart={handleInitialize}
            isLoading={isInitializing}
            warehouseId={state.config.warehouseId}
            ruleGroupId={state.config.workflows.picking.ruleGroupId}
            pickingEnabled={state.config.workflows.picking.enabled}
            onOpenConfig={() => setShowConfig(true)}
          />
        )}

        {/* Running/Paused dashboard */}
        {(state.status === 'running' || state.status === 'paused') && (
          <SimulatorDashboard state={state} />
        )}

        {/* Stopped view */}
        {state.status === 'stopped' && (
          <StoppedView state={state} onReset={handleReset} />
        )}

        {/* Error view */}
        {state.status === 'error' && (
          <ErrorView state={state} onReset={handleReset} />
        )}
      </div>

      {/* Config Modal */}
      {showConfig && (
        <SimulatorConfigPanel
          config={state.config}
          onSave={handleConfigSave}
          onCancel={() => setShowConfig(false)}
        />
      )}
    </div>
  );
};

// Status Badge Component
const StatusBadge: React.FC<{ status: SimulatorState['status'] }> = ({ status }) => {
  const statusConfig = {
    idle: { color: 'bg-gray-100 text-gray-700', label: 'Klar' },
    validating: { color: 'bg-blue-100 text-blue-700', label: 'Validerer' },
    running: { color: 'bg-green-100 text-green-700', label: 'Kjører' },
    paused: { color: 'bg-yellow-100 text-yellow-700', label: 'Pauset' },
    stopping: { color: 'bg-orange-100 text-orange-700', label: 'Stopper' },
    stopped: { color: 'bg-gray-100 text-gray-700', label: 'Stoppet' },
    error: { color: 'bg-red-100 text-red-700', label: 'Feil' }
  };

  const config = statusConfig[status];

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// Welcome View
interface WelcomeViewProps {
  onStart: () => void;
  isLoading: boolean;
  warehouseId?: string;
  ruleGroupId?: number;
  pickingEnabled?: boolean;
  onOpenConfig: () => void;
}

const WelcomeView: React.FC<WelcomeViewProps> = ({ onStart, isLoading, warehouseId, ruleGroupId, pickingEnabled, onOpenConfig }) => {
  const hasRequiredConfig = warehouseId && (!pickingEnabled || (ruleGroupId && ruleGroupId > 0));

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <Zap className="w-20 h-20 text-purple-600 mx-auto mb-6" />
        <h2 className="text-3xl font-bold text-gray-800 mb-4">Warehouse Simulator</h2>
        <p className="text-gray-600 mb-8 leading-relaxed">
          Simuler lageraktivitet for å teste systemet, demonstrere funksjoner,
          eller generere testdata. Simulatoren bruker ekte API-kall og kan observeres
          fra andre innlogginger i sanntid.
        </p>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="p-4 bg-blue-50 rounded-lg">
            <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-sm text-gray-700">5 Virtuelle Operatører</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <Activity className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-sm text-gray-700">7 Workflow-typer</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <Clock className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <p className="text-sm text-gray-700">Sanntids Metrics</p>
          </div>
        </div>

        {/* Configuration status */}
        {!warehouseId || (pickingEnabled && (!ruleGroupId || ruleGroupId === 0)) ? (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
            <p className="text-amber-800 font-medium mb-2">Konfigurasjon påkrevd</p>
            <ul className="text-sm text-amber-700 mb-3 space-y-1">
              {!warehouseId && (
                <li className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  Du må velge et lager
                </li>
              )}
              {pickingEnabled && (!ruleGroupId || ruleGroupId === 0) && (
                <li className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  Du må velge en regelgruppe for plukking
                </li>
              )}
            </ul>
            <button
              onClick={onOpenConfig}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
            >
              Åpne Innstillinger
            </button>
          </div>
        ) : (
          <div className="mb-6 space-y-2">
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg inline-flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800 font-medium">Lager: {warehouseId}</span>
            </div>
            {pickingEnabled && ruleGroupId && ruleGroupId > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg inline-flex items-center gap-2 ml-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-800 font-medium">Regelgruppe: {ruleGroupId}</span>
              </div>
            )}
          </div>
        )}

        <button
          onClick={onStart}
          disabled={isLoading || !hasRequiredConfig}
          className={`px-8 py-4 rounded-lg text-lg font-medium flex items-center gap-3 mx-auto ${
            hasRequiredConfig
              ? 'bg-purple-600 text-white hover:bg-purple-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isLoading ? (
            <RefreshCw className="w-6 h-6 animate-spin" />
          ) : (
            <CheckCircle className="w-6 h-6" />
          )}
          Start Validering
        </button>

        <p className="mt-4 text-sm text-gray-500">
          Valideringen sjekker at alle forutsetninger er på plass før simulering kan starte.
        </p>
      </div>
    </div>
  );
};

// Stopped View
const StoppedView: React.FC<{ state: SimulatorState; onReset: () => void }> = ({ state, onReset }) => (
  <div className="max-w-2xl mx-auto">
    <div className="bg-white rounded-lg shadow-lg p-8 text-center">
      <Square className="w-16 h-16 text-gray-400 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Simulering Stoppet</h2>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-2xl font-bold text-gray-800">{state.metrics.totalTransactions}</p>
          <p className="text-sm text-gray-500">Transaksjoner</p>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-2xl font-bold text-gray-800">
            {Math.floor(state.metrics.elapsedTime / 60)}:{String(Math.floor(state.metrics.elapsedTime % 60)).padStart(2, '0')}
          </p>
          <p className="text-sm text-gray-500">Varighet</p>
        </div>
      </div>

      <button
        onClick={onReset}
        className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 mx-auto"
      >
        <RefreshCw className="w-5 h-5" />
        Start På Nytt
      </button>
    </div>
  </div>
);

// Error View
const ErrorView: React.FC<{ state: SimulatorState; onReset: () => void }> = ({ state, onReset }) => (
  <div className="max-w-2xl mx-auto">
    <div className="bg-white rounded-lg shadow-lg p-8 text-center">
      <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Simulering Feilet</h2>

      {state.lastError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
          <p className="text-red-700">{state.lastError}</p>
        </div>
      )}

      <button
        onClick={onReset}
        className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 mx-auto"
      >
        <RefreshCw className="w-5 h-5" />
        Prøv Igjen
      </button>
    </div>
  </div>
);

export default SimulatorMain;
